import Cache from './cache.js';
import { buildAuthHeaders } from './authHeaders.js';
import {
  clearAdminApiToken,
  ensureProductionApiToken,
  expireAdminSession,
  getRequestAuthToken
} from './auth.js';

// Cache instance for GET requests
const DEFAULT_CACHE_TTL = 10 * 60 * 1000;
const fetchCache = new Cache(DEFAULT_CACHE_TTL);
const pendingRequests = new Map();

async function ensureAuthToken(options = {}) {
  if (options.useAuth === false) {
    return null;
  }

  return ensureProductionApiToken();
}

async function parseGetSuccess(url, response, options = {}) {
  const contentType = response.headers.get('content-type');

  if (contentType && contentType.includes('text/html')) {
    console.error('GET received HTML instead of JSON for:', url);
    return null;
  }

  try {
    const data = await response.json();
    if (data && options.useCache !== false) {
      fetchCache.set(getRequestKey(url, options), data, options.cacheTTL || DEFAULT_CACHE_TTL);
    }
    return data;
  } catch (error) {
    console.error('JSON parse error for GET:', url, error.message);
    return null;
  }
}

export function _get(url, options = {}) {
  const { useCache = true, cacheTTL = DEFAULT_CACHE_TTL } = options;
  const cacheKey = getRequestKey(url, options);

  // Check cache first
  if (useCache) {
    const cached = fetchCache.get(cacheKey);
    if (cached !== null && cached !== undefined) {
      return Promise.resolve(cached);
    }
  }

  // Return pending request if already in flight
  if (pendingRequests.has(cacheKey)) {
    return pendingRequests.get(cacheKey);
  }

  // Create new request
  const request = ensureAuthToken(options)
    .then(async () => {
      const doFetch = (authToken, cacheMode = options.httpCache || "default") => fetch(url, {
        method: "GET",
        cache: cacheMode,
        headers: buildAuthHeaders(
          { ...options, authToken },
          false,
          authToken || getRequestAuthToken()
        ),
      });

      let response = await doFetch(getRequestAuthToken());

      // 304 has no body. Ask the server for the full payload instead of treating it as empty.
      if (response.status === 304) {
        response = await doFetch(getRequestAuthToken(), "no-store");
      }

      if (!response.ok) {
        if (response.status === 401) {
          handleUnauthorized(response, options);
        }
        console.warn(`GET ${response.status}:`, url);
        return null;
      }

      return parseGetSuccess(url, response, { ...options, useCache, cacheTTL });
    })
    .catch(error => {
      console.error('Fetch error:', url, error.message);
      return null;
    })
    .finally(() => pendingRequests.delete(cacheKey));

  pendingRequests.set(cacheKey, request);
  return request;
}

export function _post(url, data, options = {}) {
  return ensureAuthToken(options).then(() => sendJsonRequest(url, "POST", data, options));
}

export function _put(url, data, options = {}) {
  return ensureAuthToken(options).then(() => sendJsonRequest(url, "PUT", data, options));
}

export function _patch(url, data, options = {}) {
  return ensureAuthToken(options).then(() => sendJsonRequest(url, "PATCH", data, options));
}

export function _postForm(url, formData, options = {}) {
  return ensureAuthToken(options).then(() =>
    sendFormRequest(url, "POST", formData, options)
  );
}

export function _putForm(url, formData, options = {}) {
  return ensureAuthToken(options).then(() =>
    sendFormRequest(url, "PUT", formData, options)
  );
}

export function _delete(url, data, options = {}) {
  return ensureAuthToken(options).then(() => sendJsonRequest(url, "DELETE", data, options));
}

function sendJsonRequest(url, method, data, options = {}) {
  return fetch(url, {
    method,
    headers: getRequestHeaders(options, Boolean(data)),
    body: data === undefined ? undefined : JSON.stringify(data),
  })
    .then((response) => parseJsonResponse(response, options))
    .catch(error => {
      console.error(`${method} error:`, url, error.message);
      return { success: false, message: error.message || 'Network error occurred' };
    });
}

function sendFormRequest(url, method, formData, options = {}) {
  return fetch(url, {
    method,
    headers: getRequestHeaders(options, false),
    body: formData,
  })
    .then((response) => parseJsonResponse(response, options))
    .catch((error) => {
      console.error(`${method} form error:`, url, error.message);
      return { success: false, message: error.message || "Network error occurred" };
    });
}

async function parseJsonResponse(response, options = {}) {
  const contentType = response.headers.get('content-type');

  if (contentType && contentType.includes('text/html')) {
    const htmlText = await response.text();
    console.error('Received HTML instead of JSON:', htmlText.substring(0, 200));
    return {
      success: false,
      message: `API endpoint not found or returned HTML. Status: ${response.status}. Check if backend endpoint exists.`,
      status: response.status
    };
  }

  if (response.ok) {
    if (response.status === 204) {
      return { success: true, status: response.status };
    }

    try {
      const result = await response.json();
      return result;
    } catch (error) {
      console.error('JSON parse error:', error.message);
      return { success: false, message: 'Invalid JSON response from server' };
    }
  }

  handleUnauthorized(response, options);

  try {
    const errorData = await response.json();
    return {
      success: false,
      message: errorData.message || errorData.error || `HTTP ${response.status}: ${response.statusText}`,
      status: response.status
    };
  } catch {
    return { success: false, message: `HTTP ${response.status}: ${response.statusText}`, status: response.status };
  }
}

export function invalidateCache(urlPattern) {
  for (const key of fetchCache.cache.keys()) {
    if (key.includes(urlPattern)) {
      fetchCache.delete(key);
    }
  }
}

function getRequestHeaders(options = {}, hasJsonBody) {
  return buildAuthHeaders(options, hasJsonBody, getRequestAuthToken());
}

function getRequestKey(url, options = {}) {
  const token = options.useAuth === false
    ? "public"
    : options.authToken || getRequestAuthToken() || "anonymous";
  return `${url}::${token}`;
}

function handleUnauthorized(response, options = {}) {
  if (response.status !== 401) return;
  if (options.useAuth === false) return;
  // Optional probes (e.g. local license fallback) must not wipe a valid login.
  if (options.expireSessionOn401 === false) return;

  clearAdminApiToken();
  expireAdminSession();
  console.warn("API request unauthorized — session ended:", response.url || "");
}
